import React, { useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Button,
  alpha,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { ResponsiveLine } from "@nivo/line";

import { ChartCard } from "../shared";
import InfoButton from "../Clinical/InfoButton";
import {
  TREND_MONTHS,
  TABLE_DEFAULTS,
  formatNumber,
} from "../../constants/ceoDashboard";

// ── Helpers ──────────────────────────────────────────────────────

/** "2025-12" -> "Dec 25" */
function formatMonthLabel(monthStr) {
  const [y, m] = monthStr.split("-");
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
}

/** Medal-chip colors by rank (0-indexed). */
function getMedalColor(index) {
  switch (index) {
    case 0:
      return { bg: "#FEF3C7", text: "#D97706" }; // gold
    case 1:
      return { bg: "#F3F4F6", text: "#6B7280" }; // silver
    case 2:
      return { bg: "#FED7AA", text: "#C2410C" }; // bronze
    default:
      return { bg: "#EFF6FF", text: "#3B82F6" }; // blue
  }
}

/** Engagement % color. */
function getEngagementColor(pct) {
  if (pct > 50) return "success.main";
  if (pct >= 30) return "warning.main";
  return "error.main";
}

// ── Component ────────────────────────────────────────────────────

/**
 * Panel 5 - Trends & Top Performers
 * Nivo line chart of Care Gaps Closed by Month + Top Providers table.
 */
export default function PanelTrends({ data, loading, helpContent }) {
  const [showAll, setShowAll] = useState(false);

  // ── Loading state ────────────────────────────────────────────
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Skeleton variant="rounded" width={36} height={36} animation="wave" />
            <Skeleton variant="text" width={260} height={32} animation="wave" />
          </Stack>

          <Box
            sx={{
              display: "grid",
              gap: 2.5,
              gridTemplateColumns: { xs: "1fr", lg: "1.2fr 1fr" },
            }}
          >
            <Skeleton
              variant="rectangular"
              height={320}
              animation="wave"
              sx={{ borderRadius: 3 }}
            />
            <Skeleton
              variant="rectangular"
              height={320}
              animation="wave"
              sx={{ borderRadius: 3 }}
            />
          </Box>
        </Stack>
      </Paper>
    );
  }

  if (!data) return null;

  // ── Prepare chart data ───────────────────────────────────────
  const allMonths = data.careGapsClosedByMonth || [];
  const filteredMonths = allMonths.slice(-TREND_MONTHS);

  const chartData = [
    {
      id: "Care Gaps Closed",
      data: filteredMonths.map((d) => ({
        x: formatMonthLabel(d.month),
        y: d.count,
      })),
    },
  ];

  // ── Prepare providers ────────────────────────────────────────
  const providers = data.topProviders || [];
  const visibleProviders = showAll
    ? providers
    : providers.slice(0, TABLE_DEFAULTS.topProviders);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: "absolute",
          top: -50,
          right: -50,
          width: 150,
          height: 150,
          background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
          opacity: 0.06,
          borderRadius: "50%",
          filter: "blur(40px)",
        }}
      />

      <Stack spacing={3} sx={{ position: "relative" }}>
        {/* ── Header ──────────────────────────────────────────── */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: "success.main",
              color: "white",
            }}
          >
            <TrendingUpIcon fontSize="small" />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            Trends & Top Performers
          </Typography>
          <InfoButton helpContent={helpContent} />
        </Stack>

        {/* ── 2-column grid ───────────────────────────────────── */}
        <Box
          sx={{
            display: "grid",
            gap: 2.5,
            gridTemplateColumns: { xs: "1fr", lg: "1.2fr 1fr" },
          }}
        >
          {/* LEFT: Line Chart */}
          <ChartCard
            title="Care Gaps Closed by Month"
            subtitle="Last 18 months"
            height={280}
            color="success"
          >
            <ResponsiveLine
              data={chartData}
              margin={{ top: 10, right: 20, bottom: 50, left: 50 }}
              xScale={{ type: "point" }}
              yScale={{ type: "linear", min: "auto", max: "auto" }}
              curve="monotoneX"
              colors={["#059669"]}
              enableArea={true}
              areaOpacity={0.15}
              enablePoints={true}
              pointSize={8}
              pointColor="#059669"
              pointBorderWidth={2}
              pointBorderColor="#fff"
              enableGridX={false}
              gridYValues={5}
              axisBottom={{
                tickSize: 0,
                tickPadding: 10,
                tickRotation: -45,
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 10,
              }}
              theme={{
                axis: {
                  ticks: {
                    text: { fill: "#9CA3AF", fontSize: 11 },
                  },
                },
                grid: {
                  line: { stroke: "#E5E7EB", strokeDasharray: "4 4" },
                },
              }}
            />
          </ChartCard>

          {/* RIGHT: Top Providers Table */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <EmojiEventsIcon sx={{ color: "#F59E0B" }} />
              <Typography variant="h6" fontWeight={700}>
                Top Performers
              </Typography>
            </Stack>

            <Box sx={{ overflowX: "auto", flex: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                      Rank
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                      Provider
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 600, color: "text.secondary" }}
                    >
                      Gaps Closed
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 600, color: "text.secondary" }}
                    >
                      Engagement %
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleProviders.map((provider, index) => {
                    const medal = getMedalColor(index);
                    return (
                      <TableRow
                        key={provider.pcpNpi}
                        sx={{
                          "&:hover": { bgcolor: "action.hover" },
                          transition: "background-color 0.2s",
                        }}
                      >
                        {/* Rank medal */}
                        <TableCell sx={{ width: 48 }}>
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              bgcolor: medal.bg,
                              color: medal.text,
                              fontSize: "0.75rem",
                              fontWeight: 700,
                            }}
                          >
                            {index + 1}
                          </Avatar>
                        </TableCell>

                        {/* Provider name */}
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            noWrap
                            sx={{ maxWidth: 200 }}
                          >
                            {provider.pcpName}
                          </Typography>
                        </TableCell>

                        {/* Gaps Closed */}
                        <TableCell align="center">
                          <Chip
                            label={formatNumber(provider.gapsClosed)}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: alpha("#10B981", 0.1),
                              color: "#059669",
                            }}
                          />
                        </TableCell>

                        {/* Engagement % */}
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color={getEngagementColor(provider.engagementPct)}
                          >
                            {provider.engagementPct}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {/* Show all / collapse toggle */}
            {providers.length > TABLE_DEFAULTS.topProviders && (
              <Box sx={{ textAlign: "center", mt: 1.5 }}>
                <Button
                  size="small"
                  onClick={() => setShowAll((prev) => !prev)}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  {showAll
                    ? "Show less"
                    : `Show all ${providers.length}`}
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      </Stack>
    </Paper>
  );
}
